import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { PuffLoader, BarLoader } from "react-spinners";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useInactivityHandler from "../utils/InactivityHandler";
import { useAuth } from "../context/AuthContext";
import API_ENDPOINTS from "../config/api";

const Deposit = () => {
  const navigate = useNavigate();
  const { token, account, isLoading: authLoading } = useAuth();
  const [exchangeRate, setExchangeRate] = useState(null); // Initialize as null
  const [amountInKES, setAmountInKES] = useState("");
  const [amountInUSD, setAmountInUSD] = useState(null); // Initialize as null
  const [loading, setLoading] = useState(true); // Start in a loading state
  const [transactionInProgress, setTransactionInProgress] = useState(false);
  useInactivityHandler();

  useEffect(() => {
    const disableRightClick = (e) => e.preventDefault();
    document.addEventListener("contextmenu", disableRightClick);

    return () => {
      document.removeEventListener("contextmenu", disableRightClick);
    };
  }, []);

  useEffect(() => {
    const fetchExchangeRate = async () => {
      // Wait for auth to load and ensure token exists
      if (authLoading || !token) {
        return;
      }

      try {
        const response = await axios.get(
          `${API_ENDPOINTS.GET_EXCHANGE_RATE}?token=${token}`
        );
        setExchangeRate(response.data.exchange_rate || 0);
      } catch (error) {
        console.error("Error fetching exchange rate:", error);
        toast.error("Failed to load exchange rate");
      } finally {
        setLoading(false); // Stop loading state once the fetch is complete
      }
    };
    fetchExchangeRate();
  }, [token, authLoading]);

  const truncateToTwoDecimals = (num) => Math.floor(num * 100) / 100;

  const calculateAmountInUSD = (amount) => {
    if (!exchangeRate || exchangeRate <= 0) return; // Prevent calculation if the exchange rate is invalid
    const parsedAmount = parseFloat(amount);
    const amountAfterCommission = parsedAmount * 0.97;
    setAmountInUSD(truncateToTwoDecimals(amountAfterCommission / exchangeRate));
  };

  const handleAmountChange = (e) => {
    const amount = e.target.value;
    setAmountInKES(amount);
    if (amount) {
      calculateAmountInUSD(amount);
    } else {
      setAmountInUSD(null); // Reset to null if input is cleared
    }
  };

  const checkTransactionStatus = async (checkoutRequestId) => {
    try {
      const response = await axios.get(
        `${API_ENDPOINTS.CHECK_TRANSACTION_STATUS}?checkoutRequestId=${checkoutRequestId}`
      );
      return response.data;
    } catch (error) {
      // If 404, transaction was deleted (user cancelled)
      if (error.response && error.response.status === 404) {
        return null;
      }
      console.error("Error checking transaction status:", error);
      return null;
    }
  };

  const initiateDeposit = async () => {
    setTransactionInProgress(true);
    setLoading(true);
    try {
      const response = await axios.post(
        API_ENDPOINTS.STK_PUSH,
        {
          account,
          token,
          amountInKES: parseFloat(amountInKES),
          amountInUSD: parseFloat(amountInUSD),
        }
      );

      if (response.data.success) {
        const checkoutRequestId = response.data.checkoutRequestId;
        toast.info("Please check your phone and enter your M-Pesa PIN");
        setLoading(false);

        // Poll for transaction status
        let attempts = 0;
        const maxAttempts = 60; // Poll for up to 2 minutes (60 * 2 seconds)

        const pollInterval = setInterval(async () => {
          attempts++;

          const status = await checkTransactionStatus(checkoutRequestId);

          // Transaction not found (404) - payment failed/cancelled and was deleted
          if (!status) {
            clearInterval(pollInterval);
            setTransactionInProgress(false);
            toast.info("Payment not completed");
            setTimeout(() => navigate("/home"), 1500);
          } else if (status && status.status === 'completed') {
            clearInterval(pollInterval);
            setTransactionInProgress(false);
            toast.success(`Deposit successful! ${status.amountInUSD} USD credited to your account.`);
            // Wait 3 seconds for Deriv balance to update, then redirect
            setTimeout(() => {
              navigate("/home", { state: { refresh: true } });
            }, 3000);
          } else if (status && status.status === 'transfer_failed') {
            clearInterval(pollInterval);
            setTransactionInProgress(false);

            // Handle specific Deriv API error codes
            if (status.failureCode === 'CashierForwardError') {
              toast.error("Your cashier is locked. Please contact Deriv support to unlock it.");
            } else if (status.failureCode === 'InvalidToken') {
              toast.error("Your Deriv token has expired. Please generate a new token.");
            } else if (status.failureReason) {
              // Show the specific error message from Deriv
              toast.error(`Transfer to Deriv failed: ${status.failureReason}`);
            } else {
              toast.error("Payment received but transfer to Deriv failed. Please contact support.");
            }
          } else if (attempts >= maxAttempts) {
            clearInterval(pollInterval);
            setTransactionInProgress(false);
            toast.warning("Transaction is taking longer than expected. Please check your balance in a few minutes.");
            setTimeout(() => navigate("/home"), 2000);
          }
        }, 2000); // Poll every 2 seconds
      } else {
        setLoading(false);
        setTransactionInProgress(false);
        toast.error(response.data.message || "Deposit failed");
      }
    } catch (error) {
      console.error("Error initiating deposit:", error);

      // Extract specific error message from backend
      const errorMessage = error.response?.data?.message ||
                          error.response?.data?.error ||
                          "An error occurred during deposit";
      toast.error(errorMessage);

      setLoading(false);
      setTransactionInProgress(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center bg-gray-100 p-4">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar />

      {transactionInProgress && (
        <>
          <BarLoader color="#36D7B7" width="100%" />
          <p className="text-red-500 text-center mt-2">
            Please do not refresh the page. Your deposit is being processed.
          </p>
        </>
      )}

      {loading ? (
        <div className="flex justify-center items-center w-full h-full">
          <PuffLoader color="#36D7B7" size={60} />
        </div>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
          <h1 className="text-2xl font-bold mb-4 text-center">Deposit</h1>

          <label className="block mb-2 text-gray-700">Amount in KES:</label>
          <input
            type="number"
            value={amountInKES}
            onChange={handleAmountChange}
            className="w-full p-2 border rounded-md mb-4 disabled:bg-gray-200 disabled:cursor-not-allowed"
            placeholder="Enter amount in KES"
            disabled={transactionInProgress}
          />

          <p className="text-lg mb-4">
            USD Equivalent:{" "}
            <strong>
              {amountInUSD !== null ? `${amountInUSD} USD` : "Calculating..."}
            </strong>
          </p>

          <p className="text-sm text-gray-600 mb-4">
            Minimum deposit amount is 1 USD.
          </p>

          <button
            onClick={initiateDeposit}
            className={`w-full py-2 px-4 rounded-md transition ${
              amountInUSD && amountInUSD >= 1 && !transactionInProgress
                ? "bg-pink-600 text-white hover:bg-pink-700"
                : "bg-gray-400 text-gray-700 cursor-not-allowed"
            }`}
            disabled={!amountInUSD || amountInUSD < 1 || transactionInProgress}
          >
            {transactionInProgress ? "Processing..." : "Confirm Deposit"}
          </button>

          <button
            onClick={() => navigate("/home")}
            className="mt-4 text-teal-600 text-lg font-semibold hover:text-teal-800 transition"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default Deposit;
